import { useEffect, useMemo, useState } from "react";
import { kaylasApi } from "./kaylasApi";
import { Loader2, Camera, X } from "lucide-react";

export default function KaylasGallery() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let off = false;
    kaylasApi.photos()
      .then((d) => { if (!off) setData(d); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, []);

  const items = useMemo(() => {
    if (filter === "all") return data.items;
    return data.items.filter((i) => i.source === filter);
  }, [data, filter]);

  const grouped = useMemo(() => {
    // group by variety/title
    const m = new Map();
    for (const p of items) {
      const k = p.group || "—";
      m.set(k, [...(m.get(k) || []), p]);
    }
    return Array.from(m.entries());
  }, [items]);

  return (
    <div className="space-y-6" data-testid="mums-gallery-page">
      <header>
        <div className="mum-chip"><Camera size={12} /> Tab 8</div>
        <h1 className="font-display text-4xl mt-2">Photo Gallery</h1>
        <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">Every photo you add anywhere in the shed appears here, by variety.</p>
      </header>

      <div className="flex gap-2 flex-wrap" data-testid="mums-gallery-filter">
        {[
          { v: "all", label: "All" },
          { v: "bloom", label: "Blooms" },
          { v: "tuber", label: "Tubers" },
          { v: "journal", label: "Journal" },
        ].map((b) => (
          <button
            key={b.v}
            type="button"
            className={`mum-tab ${filter === b.v ? "is-active" : ""}`}
            onClick={() => setFilter(b.v)}
            data-testid={`gallery-tab-${b.v}`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : items.length === 0 ? (
        <div className="kaylas-card-quiet p-10 text-center" data-testid="mums-gallery-empty">
          <Camera size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">No photographs gathered yet.</p>
          <p className="text-sm text-[var(--mum-cobalt)]/70 mt-1">Add photos to your tubers, blooms or journal entries — they all land here.</p>
        </div>
      ) : (
        <div className="space-y-7">
          {grouped.map(([group, list]) => (
            <section key={group}>
              <h2 className="font-display text-2xl text-[var(--mum-sapphire)] mb-2">{group}</h2>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                {list.map((p, i) => (
                  <button
                    key={`${p.ref_id}-${i}`}
                    type="button"
                    className="aspect-square overflow-hidden rounded-lg border border-[var(--mum-cornflower)] cursor-zoom-in transition-transform hover:scale-[1.02]"
                    onClick={() => setLightbox(p)}
                    data-testid={`gallery-thumb-${p.ref_id}-${i}`}
                  >
                    <img src={p.url} alt={p.ref_name} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-[var(--mum-midnight)]/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="mums-lightbox"
        >
          <button type="button" className="absolute top-4 right-4 mum-btn-ghost" onClick={() => setLightbox(null)}><X size={14} /> Close</button>
          <figure className="max-w-3xl w-full">
            <img src={lightbox.url} alt={lightbox.ref_name} className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" />
            <figcaption className="text-[var(--mum-ivory)] text-center font-display italic mt-3">
              {lightbox.ref_name} · {lightbox.source}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
